import axios from 'axios'

import { toCamelCase } from '../helpers/utils'
import { SlimTruck } from './truck'
import { ListUser } from './user'

export enum EsignEnvelopeStatus {
  CREATED = 'CREATED',
  SENT = 'SENT',
  SIGNED = 'SIGNED',
  DECLINED = 'DECLINED',
  VOIDED = 'VOIDED',
}

export enum EsignEnvelopeVendor {
  DOCUSIGN = 'DOCUSIGN',
  DROPBOX = 'DROPBOX',
}

export interface EsignEnvelope {
  id: string
  envelopeId: string
  dateCreated: string
  dateUpdated: string
  dateSent: string
  dateSigned: string
  status: EsignEnvelopeStatus
  recipient: ListUser
  vendor: EsignEnvelopeVendor
}

export interface EsignLeaseAgreement {
  defaultRecipient: ListUser
  clientId: string
  envelope: EsignEnvelope
  hasDraft: boolean
  canPreview: boolean
  trucks: SlimTruck[]
}

export function getEsignLeaseAgreement(teamId: string | undefined): Promise<EsignLeaseAgreement> {
  const url = `/api/v3/teams/${teamId}/esign/lease-agreement/`

  return axios.get(url).then((response) => {
    return toCamelCase(response.data) as EsignLeaseAgreement
  })
}

interface EsignLeaseAgreementPayload {
  sales_owner: string
}

export function createEsignLeaseAgreement(
  teamId: string | undefined,
  payload: EsignLeaseAgreementPayload
): Promise<EsignLeaseAgreement> {
  const url = `/api/v3/teams/${teamId}/esign/lease-agreement/`

  return axios.post(url, payload).then((response) => {
    return toCamelCase(response.data) as EsignLeaseAgreement
  })
}

export function previewEsignLeaseAgreement(teamId: string | undefined, envelopeId: string): Promise<string> {
  const url = `/api/v3/teams/${teamId}/esign/${envelopeId}/lease-agreement-preview/`

  return axios.post(url).then((response) => {
    return response.data.url as string
  })
}

export function sendEsignLeaseAgreement(
  teamId: string | undefined,
  envelopeId: string
): Promise<EsignLeaseAgreement> {
  const url = `/api/v3/teams/${teamId}/esign/${envelopeId}/lease-agreement-send/`

  return axios.post(url).then((response) => {
    return toCamelCase(response.data) as EsignLeaseAgreement
  })
}

export function voidEsignLeaseAgreement(
  teamId: string | undefined,
  envelopeId: string,
  reason: string
): Promise<EsignLeaseAgreement> {
  const url = `/api/v3/teams/${teamId}/esign/${envelopeId}/lease-agreement-void/`

  return axios.post(url, { reason }).then((response) => {
    return toCamelCase(response.data) as EsignLeaseAgreement
  })
}

export function getEsignLeaseAgreementUrl(teamId: string | undefined): Promise<string> {
  const url = `/api/v3/teams/${teamId}/esign/lease-agreement-url/`

  return axios.post(url).then((response) => {
    return response.data.url
  })
}

export function syncEsignLeaseAgreement(teamId: string | undefined): Promise<EsignEnvelope> {
  const url = `/api/v3/teams/${teamId}/esign/lease-agreement-sync/`

  return axios.post(url).then((response) => {
    return toCamelCase(response.data) as EsignEnvelope
  })
}

export function resetEsignLeaseAgreement(teamId: string | undefined): Promise<EsignLeaseAgreement> {
  const url = `/api/v3/teams/${teamId}/esign/lease-agreement-reset/`

  return axios.post(url).then((response) => {
    return toCamelCase(response.data) as EsignLeaseAgreement
  })
}

export interface EsignScreeningDisclosure {
  url: string
  clientId: string
  envelope: EsignEnvelope
}

export function createEsignPspScreeningDisclosure(
  teamId: string | undefined
): Promise<EsignScreeningDisclosure> {
  const url = `/api/v3/teams/${teamId}/esign/psp-screening-disclosure/`

  return axios.post(url).then((response) => {
    return toCamelCase(response.data) as EsignScreeningDisclosure
  })
}

export function syncEsignPspScreeningDisclosure(teamId: string | undefined): Promise<EsignEnvelope> {
  const url = `/api/v3/teams/${teamId}/esign/psp-screening-disclosure-sync/`

  return axios.post(url).then((response) => {
    return toCamelCase(response.data) as EsignEnvelope
  })
}

export function createEsignDacConsent(teamId: string | undefined): Promise<EsignScreeningDisclosure> {
  const url = `/api/v3/teams/${teamId}/esign/dac-consent/`

  return axios.post(url).then((response) => {
    return toCamelCase(response.data) as EsignScreeningDisclosure
  })
}

export function syncEsignDacConsent(teamId: string | undefined): Promise<EsignEnvelope> {
  const url = `/api/v3/teams/${teamId}/esign/dac-consent-sync/`

  return axios.post(url).then((response) => {
    return toCamelCase(response.data) as EsignEnvelope
  })
}
